import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import friendsService from '../services/friendsService';
import challengeService from '../services/challengeService';
import gameRealtimeClient from '../services/gameRealtimeClient';
import { playSuccessSound } from '../assets/sounds/chessSounds';

const SocialChallengeContext = createContext(null);

export const SocialChallengeProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [relations, setRelations] = useState({
    friends: [],
    following: [],
    followers: [],
    blocked: []
  });
  const [loadingRelations, setLoadingRelations] = useState(false);

  // Challenge Modals and Notifications State
  const [challengeModalTarget, setChallengeModalTarget] = useState(null); // User object or null
  const [activeOutgoingChallenge, setActiveOutgoingChallenge] = useState(null);
  const [incomingChallenge, setIncomingChallenge] = useState(null);

  // Fetch relations from backend
  const fetchRelations = useCallback(async () => {
    if (!currentUser) return;
    try {
      setLoadingRelations(true);
      const res = await friendsService.getRelations();
      if (res.success && res.data) {
        setRelations(res.data);
      }
    } catch (err) {
      console.error('Error fetching user relations:', err);
    } finally {
      setLoadingRelations(false);
    }
  }, [currentUser]);

  // Fetch pending received & sent challenges on mount or login
  const fetchPendingChallenges = useCallback(async () => {
    if (!currentUser) return;
    try {
      const [receivedRes, sentRes] = await Promise.all([
        challengeService.getReceivedChallenges().catch(() => null),
        challengeService.getSentChallenges().catch(() => null)
      ]);

      if (receivedRes?.success && receivedRes.data?.length > 0) {
        setIncomingChallenge(receivedRes.data[0]);
      }
      if (sentRes?.success && sentRes.data?.length > 0) {
        const activeSent = sentRes.data.find(c => c.status === 'pending');
        if (activeSent) {
          setActiveOutgoingChallenge(activeSent);
        }
      }
    } catch (err) {
      console.error('Error fetching pending challenges:', err);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchRelations();
      fetchPendingChallenges();

      // Periodically refresh challenges & relations as fallback only when the browser tab is active (60s interval)
      const pollInterval = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          fetchPendingChallenges();
          fetchRelations();
        }
      }, 60000);

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          fetchPendingChallenges();
          fetchRelations();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        clearInterval(pollInterval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } else {
      setRelations({ friends: [], following: [], followers: [], blocked: [] });
      setIncomingChallenge(null);
      setActiveOutgoingChallenge(null);
    }
  }, [currentUser, fetchRelations, fetchPendingChallenges]);

  // Handle Real-time challenge and friends events multiplexed via gameRealtimeClient
  useEffect(() => {
    if (!currentUser) {
      gameRealtimeClient.identify(null);
      return;
    }

    const userId = currentUser.id || currentUser.userId || currentUser.user_id;
    if (!userId) return;

    const token = localStorage.getItem('token');
    gameRealtimeClient.identify(userId, token);

    const unsubscribe = gameRealtimeClient.addGlobalMessageListener((data) => {
      try {
        console.log('📩 [SocialChallenge] Event received via gameRealtimeClient:', data);

        if (data.type === 'challenge:created') {
          setIncomingChallenge(data.challenge);
          try {
            playSuccessSound();
          } catch (soundErr) {
            console.warn('Could not play notification sound:', soundErr);
          }
        } else if (data.type === 'challenge:accepted') {
          setActiveOutgoingChallenge(null);
          setIncomingChallenge(null);
          setChallengeModalTarget(null);

          // Redirect both players to live board room seamlessly without full page reload
          const gameId = data.gameId;
          const myUid = userId;
          const color = String(data.whitePlayerId) === String(myUid) ? 'white' : 'black';
          navigate(`/play-online-friends?gameCode=${gameId}&autoStart=true&color=${color}`);
        } else if (data.type === 'challenge:declined') {
          setActiveOutgoingChallenge(prev => (prev && (prev.id === data.invitationId || prev._id === data.invitationId) ? { ...prev, status: 'declined' } : prev));
        } else if (data.type === 'challenge:canceled') {
          setIncomingChallenge(prev => (prev && (prev.id === data.invitationId || prev._id === data.invitationId) ? null : prev));
        } else if (data.type === 'friends:updated') {
          fetchRelations();
        }
      } catch (err) {
        console.error('Error handling SocialChallenge message:', err);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser?.id || currentUser?.userId || currentUser?.user_id, fetchRelations]);

  // Open Challenge Setup in Play With Friends panel
  const openChallengeModal = useCallback((targetUser) => {
    setChallengeModalTarget(targetUser);
    if (targetUser) {
      const targetId = targetUser.id || targetUser;
      navigate(`/play-online-friends?challengeUserId=${targetId}`);
    } else {
      navigate('/play-online-friends');
    }
  }, [navigate]);

  const closeChallengeModal = useCallback(() => {
    setChallengeModalTarget(null);
  }, []);

  // Send a new challenge
  const sendChallenge = async (targetUserId, gameSettings) => {
    try {
      const res = await challengeService.sendChallenge(targetUserId, gameSettings);
      if (res.success && res.data) {
        setActiveOutgoingChallenge(res.data);
        setChallengeModalTarget(null);
        return res.data;
      }
      throw new Error(res.message || 'Failed to send challenge');
    } catch (err) {
      console.error('Error sending challenge:', err);
      throw err;
    }
  };

  // Accept incoming challenge
  const acceptChallenge = async (invitationId) => {
    try {
      const res = await challengeService.acceptChallenge(invitationId);
      if (res.success && res.data) {
        setIncomingChallenge(null);
        setActiveOutgoingChallenge(null);
        setChallengeModalTarget(null);
        const { game, acceptorColor } = res.data;
        const gameId = game.id || game.game_code;
        navigate(`/play-online-friends?gameCode=${gameId}&autoStart=true&color=${acceptorColor}`);
        return res.data;
      }
      throw new Error(res.message || 'Failed to accept challenge');
    } catch (err) {
      console.error('Error accepting challenge:', err);
      throw err;
    }
  };

  // Decline incoming challenge
  const declineChallenge = async (invitationId, reason) => {
    try {
      await challengeService.declineChallenge(invitationId, reason);
      setIncomingChallenge(null);
    } catch (err) {
      console.error('Error declining challenge:', err);
    }
  };

  // Cancel outgoing challenge
  const cancelChallenge = async (invitationId) => {
    try {
      await challengeService.cancelChallenge(invitationId);
      setActiveOutgoingChallenge(null);
    } catch (err) {
      console.error('Error canceling challenge:', err);
    }
  };

  // Clear all pending/outgoing challenge states
  const clearAllChallenges = useCallback(() => {
    setActiveOutgoingChallenge(null);
    setIncomingChallenge(null);
    setChallengeModalTarget(null);
  }, []);

  return (
    <SocialChallengeContext.Provider
      value={{
        relations,
        loadingRelations,
        fetchRelations,
        challengeModalTarget,
        openChallengeModal,
        closeChallengeModal,
        activeOutgoingChallenge,
        setActiveOutgoingChallenge,
        incomingChallenge,
        setIncomingChallenge,
        sendChallenge,
        acceptChallenge,
        declineChallenge,
        cancelChallenge,
        clearAllChallenges
      }}
    >
      {children}
    </SocialChallengeContext.Provider>
  );
};

export const useSocialChallenge = () => {
  const context = useContext(SocialChallengeContext);
  if (!context) {
    throw new Error('useSocialChallenge must be used within a SocialChallengeProvider');
  }
  return context;
};

export default SocialChallengeContext;
